import React, { useState, useEffect } from 'react';
import { Menu, X, Home, MapPin, Search, RefreshCw, Plus, Users, ShoppingBag, Package, FileText, Download, Truck, BarChart3, Landmark } from 'lucide-react';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import TripManager from './components/TripManager';
import SearchModule from './components/SearchModule';
import LogisticsDashboard from './components/LogisticsDashboard';
import BanksReport from './components/BanksReport';
import Consolidado from './components/Consolidado';
import VersionUpdateNotification from './components/VersionUpdateNotification';
import ErrorBoundary from './components/ErrorBoundary';
import apiService from './services/apiService';
import { useHeartbeat } from './hooks/useHeartbeat';
import useFrontendVersionCheck from './hooks/useFrontendVersionCheck';
import { errorLog, warningLog, featureFlags } from './config/featureFlags';

function App() {
  const isProduction = process.env.NODE_ENV === 'production';
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isServiceDisabled, setIsServiceDisabled] = useState(false);
  const [currentPage, setCurrentPage] = useState('trips');
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [selectedEnvivo, setSelectedEnvivo] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [consolidadoSelectedTrips, setConsolidadoSelectedTrips] = useState([]); // Viajes seleccionados en Consolidado
  const [allTrips, setAllTrips] = useState([]); // Todos los viajes para mostrar nombres

  // Hook para enviar heartbeat de actividad del usuario
  // Obtener token de forma reactiva para que se actualice cuando cambie
  const [token, setToken] = useState(() => localStorage.getItem('makana_token'));
  
  useHeartbeat(isServiceDisabled ? null : token);

  // Hook para verificar actualizaciones del frontend
  const {
    versionInfo,
    showUpdatePrompt,
    updateToNewVersion,
    ignoreUpdate,
    setShowUpdatePrompt
  } = useFrontendVersionCheck();

  useEffect(() => {
    const resolveServiceStatus = async () => {
      // Solo aplica en producción: por defecto está deshabilitado temporalmente.
      if (!isProduction) {
        return false;
      }

      let shouldDisableService = featureFlags.enableMaintenanceMode;

      try {
        const backendFlags = await apiService.featureFlags.getPublic();

        const maintenanceFlag = backendFlags?.maintenanceMode ??
          backendFlags?.maintenance_mode ??
          backendFlags?.serviceTemporarilyDisabled ??
          backendFlags?.service_temporarily_disabled;

        const serviceEnabledFlag = backendFlags?.serviceEnabled ??
          backendFlags?.service_enabled;

        if (typeof maintenanceFlag === 'boolean') {
          shouldDisableService = maintenanceFlag;
        } else if (typeof serviceEnabledFlag === 'boolean') {
          shouldDisableService = !serviceEnabledFlag;
        }
      } catch (error) {
        warningLog('No se pudieron cargar feature flags del backend, se mantiene modo local.', error);
      }

      return shouldDisableService;
    };

    const initializeApp = async () => {
      const disableService = await resolveServiceStatus();
      setIsServiceDisabled(disableService);

      if (disableService) {
        // Bloqueo global: evita uso con sesión iniciada y detiene tareas.
        localStorage.removeItem('makana_token');
        localStorage.removeItem('makana_user');
        setToken(null);
        setIsAuthenticated(false);
        setUser(null);
        setLoading(false);
        return;
      }

      const isAuth = apiService.auth.isAuthenticated();
      const currentUser = apiService.auth.getCurrentUser();

      setIsAuthenticated(isAuth);
      setUser(currentUser);
      setLoading(false);

      // ✅ Cargar viajes SOLO si el usuario está autenticado
      if (isAuth) {
        loadTrips();
      }
    };

    initializeApp();
  }, [isProduction]);

  const loadTrips = async () => {
    try {
      const data = await apiService.trip.getAll();
      setAllTrips(Array.isArray(data) ? data : []);
    } catch (err) {
      errorLog('Error cargando viajes:', err);
    }
  };


  const handleLoginSuccess = (data) => {
    setIsAuthenticated(true);
    setUser(data.user);
    // Actualizar token para que el heartbeat se active
    setToken(localStorage.getItem('makana_token'));
    // ✅ Cargar viajes después del login exitoso
    loadTrips();
  };

  const handleLogout = () => {
    apiService.auth.logout();
    setIsAuthenticated(false);
    setUser(null);
    // Limpiar token para que el heartbeat se detenga
    setToken(null);
  };

  // Función para manejar la selección de viaje con navegación automática
  const handleTripSelect = (trip) => {
    setSelectedTrip(trip);
    // Navegar automáticamente al Dashboard después de seleccionar un viaje
    setCurrentPage('dashboard');
  };

  // Función para manejar acciones rápidas desde el menú
  const handleQuickAction = (action) => {
    setIsMobileMenuOpen(false);
    
    // Enviar acción al Dashboard a través de un evento personalizado
    if (currentPage === 'dashboard') {
      window.dispatchEvent(new CustomEvent('quickAction', { detail: action }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  if (isServiceDisabled) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-xl text-center">
          <div className="flex justify-center mb-5">
            <img
              src="/logo.jpg"
              alt="Makana Logo"
              className="w-16 h-16 rounded-2xl object-cover shadow-lg"
            />
          </div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-4">
            Makana Live Manager
          </h1>
          <p className="text-gray-700 text-lg">
            Servicio deshabilitado temporalmente.
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  // Función para renderizar el contenido según la página actual
  const renderCurrentPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return (
          <Dashboard 
            user={user} 
            onLogout={handleLogout}
            selectedTrip={selectedTrip}
            onTripSelect={handleTripSelect}
            selectedEnvivo={selectedEnvivo}
            onEnvivoSelect={setSelectedEnvivo}
          />
        );
      case 'consolidado':
        return (
          <Consolidado 
            user={user}
            selectedTrip={selectedTrip}
            onTripSelect={handleTripSelect}
            onSelectedTripsChange={setConsolidadoSelectedTrips}
          />
        );
      case 'search':
        return <SearchModule />;
      case 'logistics':
        return <LogisticsDashboard />;
      case 'banks-report':
        return <BanksReport />;
      case 'trips':
      default:
        return (
          <TripManager 
            onTripSelect={handleTripSelect}
            selectedTripId={selectedTrip?.id}
          />
        );
    }
  };

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 overflow-x-hidden">
      {/* Navegación Global */}
      <nav className="bg-white/98 backdrop-blur-lg border-b border-gray-100 shadow-lg fixed top-0 left-0 right-0 z-50 will-change-transform w-full">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="flex items-center justify-between h-16">
            
            {/* Logo */}
            <div className="flex items-center gap-2 md:gap-3">
              <img 
                src="/logo.jpg" 
                alt="Makana Logo" 
                className="w-7 h-7 md:w-8 md:h-8 rounded-lg object-cover shadow-md"
              />
              <div className="flex items-center gap-1 md:gap-2 flex-wrap">
                <h1 className="text-sm md:text-base lg:text-lg font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                  Makana Live Manager
                </h1>
                {/* Mostrar viajes seleccionados en Consolidado */}
                {currentPage === 'consolidado' && consolidadoSelectedTrips.length > 0 && (
                  <>
                    {consolidadoSelectedTrips.map((tripId, index) => {
                      const trip = allTrips.find(t => t.id === tripId);
                      if (!trip) return null;
                      const tripName = trip.name?.replace(/-$/, '') || `Viaje ${tripId}`;
                      return (
                        <span 
                          key={tripId} 
                          className="px-1.5 py-0.5 md:px-2 md:py-1 text-xs font-medium bg-indigo-100 text-indigo-700 rounded-md whitespace-nowrap"
                        >
                          {tripName}
                        </span>
                      );
                    })}
                  </>
                )}
                {/* Mostrar en vivo seleccionado en Dashboard */}
                {currentPage === 'dashboard' && selectedEnvivo && (
                  <span className="px-1.5 py-0.5 md:px-2 md:py-1 text-xs font-medium bg-purple-100 text-purple-700 rounded-md">
                    {selectedEnvivo.name}
                  </span>
                )}
              </div>
            </div>

            {/* Navegación Desktop */}
            <div className="hidden md:flex items-center gap-0.5 lg:gap-1">
              <button
                onClick={() => setCurrentPage('trips')}
                className={`px-2 py-1.5 lg:px-4 lg:py-2 rounded-lg text-xs lg:text-sm font-medium transition-colors whitespace-nowrap ${
                  currentPage === 'trips'
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                }`}
              >
                Viajes
              </button>
              
              <button
                onClick={() => setCurrentPage('dashboard')}
                className={`px-2 py-1.5 lg:px-4 lg:py-2 rounded-lg text-xs lg:text-sm font-medium transition-colors whitespace-nowrap ${
                  currentPage === 'dashboard'
                    ? 'bg-purple-100 text-purple-700'
                    : 'text-gray-600 hover:text-purple-600 hover:bg-purple-50'
                }`}
              >
                Dashboard
              </button>
              
              <button
                onClick={() => setCurrentPage('consolidado')}
                className={`px-2 py-1.5 lg:px-4 lg:py-2 rounded-lg text-xs lg:text-sm font-medium transition-colors whitespace-nowrap ${
                  currentPage === 'consolidado'
                    ? 'bg-indigo-100 text-indigo-700'
                    : 'text-gray-600 hover:text-indigo-600 hover:bg-indigo-50'
                }`}
              >
                Consolidado
              </button>
              
              <button
                onClick={() => setCurrentPage('search')}
                className={`px-2 py-1.5 lg:px-4 lg:py-2 rounded-lg text-xs lg:text-sm font-medium transition-colors whitespace-nowrap ${
                  currentPage === 'search'
                    ? 'bg-green-100 text-green-700'
                    : 'text-gray-600 hover:text-green-600 hover:bg-green-50'
                }`}
              >
                Búsqueda
              </button>
              
              <button
                onClick={() => setCurrentPage('logistics')}
                className={`px-2 py-1.5 lg:px-4 lg:py-2 rounded-lg text-xs lg:text-sm font-medium transition-colors whitespace-nowrap ${
                  currentPage === 'logistics'
                    ? 'bg-orange-100 text-orange-700'
                    : 'text-gray-600 hover:text-orange-600 hover:bg-orange-50'
                }`}
              >
                Logística
              </button>
              
              <button
                onClick={() => setCurrentPage('banks-report')}
                className={`px-2 py-1.5 lg:px-4 lg:py-2 rounded-lg text-xs lg:text-sm font-medium transition-colors whitespace-nowrap ${
                  currentPage === 'banks-report'
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'text-gray-600 hover:text-emerald-600 hover:bg-emerald-50'
                }`}
              >
                Reporte Bancos
              </button>
            </div>

            {/* Usuario y Menu */}
            <div className="flex items-center gap-2 md:gap-3">
              <span className="hidden sm:block text-xs md:text-sm text-gray-600 whitespace-nowrap">Hola, {user?.username}</span>
              
              {/* Botón Logout Desktop */}
              <button
                onClick={handleLogout}
                className="hidden sm:block px-2 py-1 md:px-3 md:py-1 text-xs md:text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors whitespace-nowrap"
              >
                Cerrar Sesión
              </button>

              {/* Botón Menu Hamburguesa */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
              >
                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {/* Menú Móvil Desplegable */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
            <div className="px-4 py-3 space-y-1">
              
              {/* Navegación Principal */}
              <div className="pb-2 mb-2 border-b border-gray-100">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  Navegación
                </h3>
                <button
                  onClick={() => {
                    setCurrentPage('trips');
                    setIsMobileMenuOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    currentPage === 'trips'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                  }`}
                >
                  <MapPin size={18} />
                  Viajes
                </button>
                
                <button
                  onClick={() => {
                    setCurrentPage('dashboard');
                    setIsMobileMenuOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    currentPage === 'dashboard'
                      ? 'bg-purple-100 text-purple-700'
                      : 'text-gray-600 hover:text-purple-600 hover:bg-purple-50'
                  }`}
                >
                  <Home size={18} />
                  Dashboard
                </button>
                
                <button
                  onClick={() => {
                    setCurrentPage('consolidado');
                    setIsMobileMenuOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    currentPage === 'consolidado'
                      ? 'bg-indigo-100 text-indigo-700'
                      : 'text-gray-600 hover:text-indigo-600 hover:bg-indigo-50'
                  }`}
                >
                  <BarChart3 size={18} />
                  Consolidado
                </button>
                
                <button
                  onClick={() => {
                    setCurrentPage('search');
                    setIsMobileMenuOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    currentPage === 'search'
                      ? 'bg-green-100 text-green-700'
                      : 'text-gray-600 hover:text-green-600 hover:bg-green-50'
                  }`}
                >
                  <Search size={18} />
                  Búsqueda
                </button>
                
                <button
                  onClick={() => {
                    setCurrentPage('logistics');
                    setIsMobileMenuOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    currentPage === 'logistics'
                      ? 'bg-orange-100 text-orange-700'
                      : 'text-gray-600 hover:text-orange-600 hover:bg-orange-50'
                  }`}
                >
                  <Truck size={18} />
                  Logística
                </button>
                
                <button
                  onClick={() => {
                    setCurrentPage('banks-report');
                    setIsMobileMenuOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    currentPage === 'banks-report'
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'text-gray-600 hover:text-emerald-600 hover:bg-emerald-50'
                  }`}
                >
                  <Landmark size={18} />
                  Reporte Bancos
                </button>
              </div>

              {/* Acciones Rápidas (solo en Dashboard) */}
              {currentPage === 'dashboard' && (
                <div className="pb-2 mb-2 border-b border-gray-100">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    Acciones Rápidas
                  </h3>
                  
                  <button
                    onClick={() => handleQuickAction('refresh')}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:text-green-600 hover:bg-green-50 transition-colors"
                  >
                    <RefreshCw size={18} />
                    Actualizar
                  </button>
                  
                  <button
                    onClick={() => handleQuickAction('newSession')}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:text-purple-600 hover:bg-purple-50 transition-colors"
                  >
                    <Plus size={18} />
                    Nueva Sesión
                  </button>
                  
                  <button
                    onClick={() => handleQuickAction('addClients')}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                  >
                    <Users size={18} />
                    Agregar Clientes
                  </button>
                  
                  <button
                    onClick={() => handleQuickAction('quickOrder')}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:text-green-600 hover:bg-green-50 transition-colors"
                  >
                    <ShoppingBag size={18} />
                    Pedido Rápido
                  </button>
                  
                  <button
                    onClick={() => handleQuickAction('quickProduct')}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:text-purple-600 hover:bg-purple-50 transition-colors"
                  >
                    <Package size={18} />
                    Producto Rápido
                  </button>
                  
                  <button
                    onClick={() => handleQuickAction('manualOrder')}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:text-orange-600 hover:bg-orange-50 transition-colors"
                  >
                    <FileText size={18} />
                    Pedido Manual
                  </button>
                  
                  <button
                    onClick={() => handleQuickAction('exportExcel')}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:text-green-600 hover:bg-green-50 transition-colors"
                  >
                    <Download size={18} />
                    Exportar Excel
                  </button>
                </div>
              )}

              {/* Usuario y Logout en móvil */}
              <div className="pt-2">
                <div className="px-3 py-2 text-sm text-gray-600">
                  Hola, {user?.username}
                </div>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 transition-colors"
                >
                  <X size={18} />
                  Cerrar Sesión
                </button>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Contenido */}
      <div className="max-w-screen-2xl mx-auto pt-16 w-full overflow-x-hidden">
        {renderCurrentPage()}
      </div>

      {/* Notificación de actualización de versión */}
      <VersionUpdateNotification
        showUpdatePrompt={showUpdatePrompt}
        versionInfo={versionInfo}
        updateToNewVersion={updateToNewVersion}
        ignoreUpdate={ignoreUpdate}
        setShowUpdatePrompt={setShowUpdatePrompt}
      />
      </div>
    </ErrorBoundary>
  );
}

export default App;